import React, { lazy, Suspense } from "react";
import clsx from "clsx";
import makeStyles from "@material-ui/styles/makeStyles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import Container from "@material-ui/core/Container";

import "swiper/css";
import "swiper/css/navigation";

const ReactSVGIcon = lazy(() =>
	import(/* webpackChunkName: "reactSVGIcon" */ "@micado-digital/react-svg-icon/ReactSVGIcon")
);
const Item = lazy(() => import(/* webpackChunkName: "reactKeyfactsItem" */ "./item"));

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(0),
		"& .swiper-container": {
			padding: theme.spacing(0, 4),
			"@media (min-width: 1784px)": {
				marginLeft: theme.spacing(8.5),
				marginRight: theme.spacing(8.5)
			}
		},
		[theme.breakpoints.up("md")]: {
			marginLeft: 0,
			marginRight: 0
		},
		[theme.breakpoints.up("lg")]: {
			"& .swiper-container": {
				padding: theme.spacing(0, 6)
			}
		}
	},
	slider: {
		"& .swiper-slide": {
			opacity: 0,
			transition: "opacity 1000ms ease-out"
		},
		"& .swiper-slide-active": {
			opacity: 1
		},
		"& .swiper-slide .teaser-number": {
			opacity: 0,
			transition: "opacity 1500ms ease .6s"
		},
		"& .swiper-slide-active .teaser-number": {
			opacity: 0.8
		},
		[theme.breakpoints.up("sm")]: {
			"& .swiper-slide-next": {
				opacity: 1
			},
			"& .swiper-slide-next .teaser-number": {
				opacity: 0.8
			}
		},
		[theme.breakpoints.up("md")]: {
			"& .swiper-slide-next": {
				opacity: 0
			},
			"& .swiper-slide-next .teaser-number": {
				opacity: 0
			}
		}
	},
	swiperNav: {
		left: "-6px !important",
		"&+ .swiper-button-next": {
			left: "auto !important",
			right: -6,
			[theme.breakpoints.up("lg")]: {
				right: "0px !important"
			}
		},
		[theme.breakpoints.up("lg")]: {
			left: "0px !important"
		},
		"&:after": {
			display: "none"
		}
	}
}));

const TeaserSlider = ({ items, counter = false, hasMarks = false, lang }) => {
	const screenSM = useMediaQuery(theme => theme.breakpoints.only("sm"));
	const screenMD = useMediaQuery(theme => theme.breakpoints.up("md"));
	const screenLG = useMediaQuery(theme => theme.breakpoints.up("lg"));
	const css = useStyles();

	const id = Math.floor(Math.random() * 100);

	const options = {
		spaceBetween: function () {
			if (screenMD) return 48;
			if (screenLG) return 70;
			return 32;
		},
		slidesPerView: function () {
			if (screenSM) return 2;
			return 1;
		}
	};

	return (
		<>
			{items && (
				<Container className={css.root} maxWidth={false}>
					<Swiper
						className={clsx(css.slider, "swiper-container")}
						observer={true}
						observeParents={true}
						slidesPerView={options.slidesPerView()}
						spaceBetween={options.spaceBetween()}
						speed={1000}
						modules={[Navigation]}
						navigation={{
							nextEl: `.swiper-button-next--${id}`,
							prevEl: `.swiper-button-prev--${id}`
						}}
					>
						{items.map(({ addition, link, media, target, text, title, marks }, index) => {
							return (
								<SwiperSlide key={index}>
									<Suspense fallback={<></>}>
										<Item
											addition={addition}
											counter={counter}
											index={index}
											lang={lang}
											link={link}
											mark={hasMarks ? marks : null}
											media={media}
											target={target}
											text={text}
											title={title}
										/>
									</Suspense>
								</SwiperSlide>
							);
						})}
						<>
							<div
								className={clsx(
									`swiper-button-prev`,
									`swiper-button-prev--${id}`,
									css.swiperNav
								)}
							>
								<Suspense fallback={<></>}>
									<ReactSVGIcon
										color="primary"
										src="/img/icons/arrow-left.svg"
										size={screenLG ? 65 : 40}
									/>
								</Suspense>
							</div>
							<div
								className={clsx(
									`swiper-button-next`,
									`swiper-button-next--${id}`,
									css.swiperNav
								)}
							>
								<Suspense fallback={<></>}>
									<ReactSVGIcon
										color="primary"
										src="/img/icons/arrow-right.svg"
										size={screenLG ? 65 : 40}
									/>
								</Suspense>
							</div>
						</>
					</Swiper>
				</Container>
			)}
		</>
	);
};

export default TeaserSlider;
